import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import SafeIcon from '../../common/SafeIcon';
import { supabase, isSupabaseConfigured } from '../../lib/supabase';
import { BOUQUET_SIZES, ADDONS, DELIVERY_FEE, FREE_DELIVERY_THRESHOLD, TAX_RATE } from '../../lib/pricing';

function getTodayStr() {
  return new Date().toISOString().split('T')[0];
}

function getTomorrowStr() {
  const d = new Date();
  d.setDate(d.getDate() + 1);
  return d.toISOString().split('T')[0];
}

function formatHumanDate(dateStr) {
  if (!dateStr) return '';
  const [y, m, d] = dateStr.split('-').map(Number);
  return new Date(y, m - 1, d).toLocaleDateString('en-US', {
    weekday: 'short', month: 'short', day: 'numeric',
  });
}

export default function DeliverySettingsPanel() {
  const [settings, setSettings] = useState({
    next_day_daily_limit: 5,
    scheduled_daily_limit: 10,
  });
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [saveError, setSaveError] = useState('');
  const [loading, setLoading] = useState(true);
  const [capacityPreview, setCapacityPreview] = useState([]);
  const [loadingPreview, setLoadingPreview] = useState(true);

  useEffect(() => {
    fetchSettings();
    fetchCapacityPreview();
  }, []);

  async function fetchSettings() {
    setLoading(true);
    if (isSupabaseConfigured()) {
      try {
        const { data, error } = await supabase
          .from('delivery_settings')
          .select('*')
          .eq('id', 1)
          .single();
        if (error) throw error;
        if (data) setSettings({
          next_day_daily_limit: data.next_day_daily_limit,
          scheduled_daily_limit: data.scheduled_daily_limit,
        });
      } catch (err) {
        console.error('Failed to load settings:', err);
      }
    }
    setLoading(false);
  }

  async function fetchCapacityPreview() {
    setLoadingPreview(true);
    if (!isSupabaseConfigured()) {
      setCapacityPreview([
        { date: getTomorrowStr(), next_day_count: 2, scheduled_count: 1 },
      ]);
      setLoadingPreview(false);
      return;
    }

    try {
      const today = getTodayStr();
      const future = new Date();
      future.setDate(future.getDate() + 14);
      const futureStr = future.toISOString().split('T')[0];

      const { data } = await supabase
        .from('orders')
        .select('delivery_date, delivery_type')
        .gte('delivery_date', today)
        .lte('delivery_date', futureStr)
        .not('status', 'eq', 'cancelled');

      const grouped = {};
      (data || []).forEach(order => {
        if (!grouped[order.delivery_date]) {
          grouped[order.delivery_date] = { next_day_count: 0, scheduled_count: 0 };
        }
        if (order.delivery_type === 'next_day') {
          grouped[order.delivery_date].next_day_count++;
        } else {
          grouped[order.delivery_date].scheduled_count++;
        }
      });

      const preview = Object.entries(grouped)
        .sort(([a], [b]) => a.localeCompare(b))
        .map(([date, counts]) => ({ date, ...counts }));

      setCapacityPreview(preview);
    } catch (err) {
      console.error('Failed to load capacity preview:', err);
    } finally {
      setLoadingPreview(false);
    }
  }

  async function handleSave() {
    setSaving(true);
    setSaved(false);
    setSaveError('');
    if (isSupabaseConfigured()) {
      try {
        const { error } = await supabase
          .from('delivery_settings')
          .update({
            next_day_daily_limit: settings.next_day_daily_limit,
            scheduled_daily_limit: settings.scheduled_daily_limit,
            updated_at: new Date().toISOString(),
          })
          .eq('id', 1);
        if (error) throw error;
        setSaved(true);
        setTimeout(() => setSaved(false), 3000);
      } catch (err) {
        console.error('Failed to save settings:', err);
        setSaveError('Save failed. Make sure the UPDATE policy in Supabase allows admin access to delivery_settings.');
      }
    } else {
      setSaved(true);
      setTimeout(() => setSaved(false), 3000);
    }
    setSaving(false);
  }

  const tomorrow = getTomorrowStr();

  return (
    <div className="space-y-8 max-w-2xl">
      <div>
        <h2 className="text-2xl font-serif text-charcoal">Delivery Settings</h2>
        <p className="text-sage-500 text-sm mt-1">
          Control how many orders can be placed per day to match your driver capacity.
        </p>
      </div>

      {/* Daily Limits Card */}
      <div className="bg-white rounded-2xl border border-sage-100 p-8 space-y-8 shadow-soft">
        <div className="flex items-center gap-3 border-b border-sage-50 pb-5">
          <div className="w-10 h-10 bg-amber-50 rounded-full flex items-center justify-center">
            <SafeIcon name="Truck" className="text-amber-400" />
          </div>
          <div>
            <h3 className="font-serif text-xl text-charcoal">Daily Order Limits</h3>
            <p className="text-sage-400 text-xs font-medium">Per delivery day, per order type</p>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
          {/* Next Day Limit */}
          <div className="space-y-3">
            <div className="flex items-center gap-2">
              <span className="text-lg">⚡</span>
              <label className="text-sm font-bold text-charcoal uppercase tracking-wider">Next Day Limit</label>
            </div>
            <p className="text-xs text-sage-400">
              Max next-day orders per day. When full, next-day shows as unavailable to customers.
            </p>
            <div className="flex items-center gap-4">
              <button
                onClick={() => setSettings(s => ({ ...s, next_day_daily_limit: Math.max(1, s.next_day_daily_limit - 1) }))}
                className="w-10 h-10 rounded-full bg-sage-100 text-charcoal font-bold text-xl flex items-center justify-center hover:bg-sage-200 transition-colors"
              >−</button>
              <span className="text-4xl font-serif text-charcoal w-12 text-center">
                {settings.next_day_daily_limit}
              </span>
              <button
                onClick={() => setSettings(s => ({ ...s, next_day_daily_limit: s.next_day_daily_limit + 1 }))}
                className="w-10 h-10 rounded-full bg-sage-100 text-charcoal font-bold text-xl flex items-center justify-center hover:bg-sage-200 transition-colors"
              >+</button>
            </div>
            <p className="text-[10px] text-sage-400 uppercase tracking-widest font-bold">orders / day</p>
          </div>

          {/* Scheduled Limit */}
          <div className="space-y-3">
            <div className="flex items-center gap-2">
              <SafeIcon name="Calendar" className="text-blush-300" />
              <label className="text-sm font-bold text-charcoal uppercase tracking-wider">Scheduled Limit</label>
            </div>
            <p className="text-xs text-sage-400">
              Max scheduled orders per delivery date. Fully booked dates are blocked at checkout.
            </p>
            <div className="flex items-center gap-4">
              <button
                onClick={() => setSettings(s => ({ ...s, scheduled_daily_limit: Math.max(1, s.scheduled_daily_limit - 1) }))}
                className="w-10 h-10 rounded-full bg-sage-100 text-charcoal font-bold text-xl flex items-center justify-center hover:bg-sage-200 transition-colors"
              >−</button>
              <span className="text-4xl font-serif text-charcoal w-12 text-center">
                {settings.scheduled_daily_limit}
              </span>
              <button
                onClick={() => setSettings(s => ({ ...s, scheduled_daily_limit: s.scheduled_daily_limit + 1 }))}
                className="w-10 h-10 rounded-full bg-sage-100 text-charcoal font-bold text-xl flex items-center justify-center hover:bg-sage-200 transition-colors"
              >+</button>
            </div>
            <p className="text-[10px] text-sage-400 uppercase tracking-widest font-bold">orders / day</p>
          </div>
        </div>

        {saveError && (
          <div className="bg-red-50 border border-red-200 rounded-xl px-4 py-3">
            <p className="text-red-600 text-xs font-bold">{saveError}</p>
          </div>
        )}

        <button
          onClick={handleSave}
          disabled={saving}
          className="btn-primary w-full flex items-center justify-center gap-2"
        >
          {saving ? (
            <><span className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />Saving…</>
          ) : saved ? (
            <><SafeIcon name="Check" /> Saved!</>
          ) : (
            <><SafeIcon name="Save" /> Save Settings</>
          )}
        </button>
      </div>

      {/* Capacity Preview */}
      <div className="bg-white rounded-2xl border border-sage-100 p-8 space-y-5 shadow-soft">
        <div className="flex items-center justify-between">
          <div>
            <h3 className="font-serif text-xl text-charcoal">Upcoming Capacity</h3>
            <p className="text-sage-400 text-xs font-medium">Orders booked for the next 14 days</p>
          </div>
          <button
            onClick={fetchCapacityPreview}
            className="text-xs text-blush-400 font-bold hover:text-blush-500 flex items-center gap-1"
          >
            <SafeIcon name="RefreshCw" className="text-sm" /> Refresh
          </button>
        </div>

        {loadingPreview ? (
          <div className="flex justify-center py-6">
            <div className="w-6 h-6 border-2 border-blush-200 border-t-blush-400 rounded-full animate-spin" />
          </div>
        ) : capacityPreview.length === 0 ? (
          <div className="text-center py-8 text-sage-400">
            <SafeIcon name="Calendar" className="text-4xl mb-2 mx-auto" />
            <p className="text-sm font-medium">No upcoming orders</p>
          </div>
        ) : (
          <div className="space-y-3">
            {capacityPreview.map(({ date, next_day_count, scheduled_count }) => {
              const nextDayPct = Math.min(100, (next_day_count / settings.next_day_daily_limit) * 100);
              const scheduledPct = Math.min(100, (scheduled_count / settings.scheduled_daily_limit) * 100);
              const isFullNextDay = next_day_count >= settings.next_day_daily_limit;
              const isFullScheduled = scheduled_count >= settings.scheduled_daily_limit;

              return (
                <motion.div
                  key={date}
                  initial={{ opacity: 0, x: -10 }}
                  animate={{ opacity: 1, x: 0 }}
                  className={`rounded-xl p-4 border ${
                    isFullNextDay || isFullScheduled
                      ? 'border-red-100 bg-red-50/40'
                      : date === tomorrow
                      ? 'border-amber-100 bg-amber-50/40'
                      : 'border-sage-100 bg-sage-50/30'
                  }`}
                >
                  <div className="flex items-center justify-between mb-3">
                    <div className="flex items-center gap-2">
                      <span className="font-bold text-sm text-charcoal">{formatHumanDate(date)}</span>
                      {date === tomorrow && (
                        <span className="text-[10px] bg-amber-100 text-amber-600 font-bold px-2 py-0.5 rounded-full uppercase tracking-wider">Tomorrow</span>
                      )}
                    </div>
                    {(isFullNextDay || isFullScheduled) && (
                      <span className="text-[10px] bg-red-100 text-red-600 font-bold px-2 py-0.5 rounded-full uppercase tracking-wider">At Capacity</span>
                    )}
                  </div>

                  <div className="space-y-2">
                    <div className="flex items-center gap-3">
                      <span className="text-xs w-20 text-sage-500 font-medium shrink-0">⚡ Next Day</span>
                      <div className="flex-1 bg-sage-100 rounded-full h-2">
                        <div
                          className={`h-2 rounded-full transition-all ${isFullNextDay ? 'bg-red-400' : 'bg-amber-400'}`}
                          style={{ width: `${nextDayPct}%` }}
                        />
                      </div>
                      <span className={`text-xs font-bold w-16 text-right shrink-0 ${isFullNextDay ? 'text-red-500' : 'text-sage-500'}`}>
                        {next_day_count} / {settings.next_day_daily_limit}
                      </span>
                    </div>
                    <div className="flex items-center gap-3">
                      <span className="text-xs w-20 text-sage-500 font-medium shrink-0">📅 Scheduled</span>
                      <div className="flex-1 bg-sage-100 rounded-full h-2">
                        <div
                          className={`h-2 rounded-full transition-all ${isFullScheduled ? 'bg-red-400' : 'bg-blush-300'}`}
                          style={{ width: `${scheduledPct}%` }}
                        />
                      </div>
                      <span className={`text-xs font-bold w-16 text-right shrink-0 ${isFullScheduled ? 'text-red-500' : 'text-sage-500'}`}>
                        {scheduled_count} / {settings.scheduled_daily_limit}
                      </span>
                    </div>
                  </div>
                </motion.div>
              );
            })}
          </div>
        )}
      </div>

      {/* Pricing Overview */}
      <div className="bg-white rounded-2xl border border-sage-100 p-8 space-y-6 shadow-soft">
        <div className="flex items-center gap-3 border-b border-sage-50 pb-5">
          <div className="w-10 h-10 bg-blush-50 rounded-full flex items-center justify-center">
            <SafeIcon name="Tag" className="text-blush-400" />
          </div>
          <div>
            <h3 className="font-serif text-xl text-charcoal">Pricing Overview</h3>
            <p className="text-sage-400 text-xs font-medium">Edit prices in <code className="bg-sage-50 px-1 py-0.5 rounded text-[11px]">src/lib/pricing.js</code></p>
          </div>
        </div>

        <div className="space-y-4">
          <p className="text-xs font-bold text-sage-400 uppercase tracking-widest">Bouquet Tiers</p>
          <div className="space-y-2">
            {BOUQUET_SIZES.map(size => (
              <div key={size.name} className="flex justify-between items-center bg-sage-50 rounded-xl px-4 py-3">
                <div className="flex items-center gap-3">
                  <span className="font-serif text-charcoal">{size.name}</span>
                  {size.badge && (
                    <span className="text-[10px] bg-blush-100 text-blush-500 font-bold px-2 py-0.5 rounded-full uppercase tracking-wider">{size.badge}</span>
                  )}
                </div>
                <span className="font-serif text-xl text-charcoal">${size.price}</span>
              </div>
            ))}
          </div>
        </div>

        <div className="space-y-4">
          <p className="text-xs font-bold text-sage-400 uppercase tracking-widest">Add-ons</p>
          <div className="space-y-2">
            {ADDONS.map(addon => (
              <div key={addon.id} className="flex justify-between items-center bg-sage-50 rounded-xl px-4 py-3">
                <span className="text-charcoal">{addon.name}</span>
                <span className="font-serif text-xl text-charcoal">${addon.price}</span>
              </div>
            ))}
          </div>
        </div>

        <div className="space-y-4">
          <p className="text-xs font-bold text-sage-400 uppercase tracking-widest">Delivery</p>
          <div className="space-y-2">
            <div className="flex justify-between items-center bg-sage-50 rounded-xl px-4 py-3">
              <span className="text-charcoal">Delivery Fee</span>
              <span className="font-serif text-xl text-charcoal">${DELIVERY_FEE}</span>
            </div>
            <div className="flex justify-between items-center bg-green-50 rounded-xl px-4 py-3">
              <span className="text-charcoal">Free Delivery Threshold</span>
              <span className="font-serif text-xl text-green-700">${FREE_DELIVERY_THRESHOLD}+</span>
            </div>
            <div className="flex justify-between items-center bg-sage-50 rounded-xl px-4 py-3">
              <div>
                <span className="text-charcoal">AZ TPT (Tax Rate)</span>
                <p className="text-xs text-sage-400 mt-0.5">Origin-based · Surprise, AZ · Delivery exempt</p>
              </div>
              <span className="font-serif text-xl text-charcoal">{(TAX_RATE * 100).toFixed(1)}%</span>
            </div>
          </div>
        </div>
      </div>

      {/* SQL hint */}
      <div className="bg-sage-50 rounded-2xl border border-sage-100 p-6 space-y-3">
        <div className="flex items-center gap-2">
          <SafeIcon name="Database" className="text-sage-400" />
          <h4 className="font-bold text-sm text-charcoal">If Save Still Fails</h4>
        </div>
        <p className="text-xs text-sage-500">Run this in Supabase SQL Editor to ensure the admin UPDATE policy exists:</p>
        <pre className="text-[11px] bg-white border border-sage-200 rounded-xl p-4 overflow-x-auto text-sage-600 leading-relaxed whitespace-pre-wrap">
{`DROP POLICY IF EXISTS "Admins can update delivery settings" ON delivery_settings;

CREATE POLICY "Admins can update delivery settings"
  ON delivery_settings FOR UPDATE
  TO authenticated
  USING (
    EXISTS (SELECT 1 FROM profiles WHERE id = auth.uid() AND is_admin = true)
  )
  WITH CHECK (
    EXISTS (SELECT 1 FROM profiles WHERE id = auth.uid() AND is_admin = true)
  );`}
        </pre>
      </div>
    </div>
  );
}
